/* eslint-disable max-lines -- temporary for the gift card hotfix; the file is split in a follow-up refactor PR */
/**
 * bookingsV2 — client + types for the v2 booking-detail endpoint
 * (`GET /api/v2/bookings/:id`, BON-145).
 *
 * Deliberately SEPARATE from `./bookings.ts` (the warehouse-shaped v1 roster
 * client): this mirrors the frozen BON-145 contract — the ops-DB `Booking`
 * shaped for the operator-facing detail view. `:id` is the ops `Booking.id`
 * UUID (never the `BKG-` display code). The endpoint returns the booking
 * object UNWRAPPED (no `{ booking: … }` envelope).
 */
import { apiFetch } from '../lib/apiFetch';

export type BookingStatus =
  | 'CONFIRMED'
  /** @deprecated The old name for REBOOKED. Retained for ONE release so a
   *  browser holding a stale bundle during the deploy window still renders a
   *  real label instead of "Unknown". Delete it the release after. */
  | 'SUPERSEDED'
  | 'REBOOKED'
  | 'REFUNDED'
  | 'CANCELLED';
export type BookingSource = 'nebari' | 'fareharbor';

export interface BookingMoney {
  subtotalCents: number;
  ticketsCount: number;
  taxesCents: number;
  /** Null or 0 when no service fee was charged. */
  feesCents: number | null;
  totalCents: number;
  /** Net cash currently attributed to this booking (ADR-0019) — NOT a receipt
   *  field. Null on imported rows FareHarbor gave no amount_paid for. This is
   *  what a refund returns and zeroes. */
  amountPaidCents: number | null;
  /** The ticket value gift passes paid for; 0 when none did. Not cash. */
  passCoveredCents: number;
  /** What the ticket sales campaign took off the seats no pass covered; 0 with none. */
  campaignDiscountCents: number;
}

/** A gift pass that paid for one seat, by its masked code. */
export interface BookingGiftPass {
  codeMasked: string;
}

export interface BookingStripe {
  /** The single money reference. BON-379 dropped `checkoutSessionId` along with
   *  the Checkout Sessions path. */
  paymentIntentId: string | null;
}

export interface BookingCustomer {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  /** The customer's OTHER bookings (excludes this record + PENDING_PAYMENT). */
  priorBookingsCount: number;
}

export interface BookingWorkshopVenue {
  name: string;
  city: string;
  state: string;
}

export interface BookingWorkshopRegion {
  id: string;
  name: string;
}

export interface BookingWorkshop {
  id: string;
  /** WorkshopType display name; nullable (a migrated row may lack a type). */
  name: string | null;
  startsAt: string;
  /** Absolute instant the workshop ENDS. The threshold both retire paths
   *  compare against — a booking on an ended workshop can be neither refunded
   *  nor cancelled — so the action can be disabled with a reason up front
   *  instead of letting the request come back 409. Compare on this; never
   *  display it (house rule: display the venue-local wall clock).
   *
   *  The backend has sent it since BON-335; this type simply never declared
   *  it. Empty string when the workshop is missing, which parses to NaN — so
   *  guard it rather than trusting the comparison. */
  endsAt: string;
  /** Naive wall-clock local start (backend TZ convention) | null. */
  localStartDatetime: string | null;
  /** "Taught by" instructor display name; null when the workshop has no crew. */
  teacherName: string | null;
  /** Tree-species name — maps to the tree-type icon/color; null when unset. */
  treeSpeciesName: string | null;
  venue: BookingWorkshopVenue;
  region: BookingWorkshopRegion | null;
  capacity: number | null;
  /** capacity − confirmed attendees, clamped ≥ 0; null when capacity unknown. */
  seatsLeft: number | null;
}

/** Minimal label fields for a neighbouring booking in the rebooking chain. */
export interface BookingChainLink {
  id: string;
  confirmationCode: string | null;
  displayRef: string | null;
  status: BookingStatus;
  /** Nullable — a migrated neighbour may lack a workshop-type name. */
  workshopName: string | null;
  startsAt: string;
}

/** The cutoff verdict for THIS booking (BON-146). Server-evaluated — knowing
 *  when 10:00 falls at a venue needs that venue's timezone, which nothing else
 *  in this payload carries.
 *
 *  `canOverrideCutoff` deliberately does NOT appear here: that is a property of
 *  the caller, not the booking, and the frontend reads it from the token. */
export interface BookingRebookCutoff {
  /** ISO UTC. Null when the venue's zone is unresolvable. */
  cutoffAt: string | null;
  cutoffPassed: boolean;
}

export interface BookingChain {
  /** Predecessor — this record was rebooked FROM it. */
  rebookedFrom: BookingChainLink | null;
  /** Successor — this record was rebooked TO / superseded by it. */
  rebookedTo: BookingChainLink | null;
}

/** The destinations a refund can go to. `CARD` (returned by hand in the
 *  Stripe dashboard) and `EXTERNAL` (any other rail — a check, a transfer,
 *  PayPal, Cash App) move no money — the CSR has already moved it outside
 *  Nebari before recording it here. `GIFT_PASS` (one new pass per seat)
 *  replaces `GIFT_CARD` while gift passes are switched on; the two are never
 *  offered together. Declared here (rather than beside `RefundBookingResult` below,
 *  where it originally lived) so `BookingDetail.refund` can reference it
 *  without a forward dependency on a type declared later in the file. */
export type RefundMethod = 'GIFT_CARD' | 'CARD' | 'EXTERNAL' | 'GIFT_PASS';

/** The settled record of a booking's refund (BON-335 round 4) — independent
 *  of `money.totalCents` (the receipt, which never changes) and
 *  `money.amountPaidCents` (zeroed by the refund). `externalReference` and
 *  `giftCardId` are mutually exclusive: at most one is non-null, matching
 *  which destination `method` names. */
export interface BookingRefund {
  amountCents: number;
  currency: string;
  method: RefundMethod;
  reason: string;
  externalReference: string | null;
  giftCardId: string | null;
  createdAt: string;
}

/** A deep link into an external system this booking also lives in (BON-546).
 *  The server supplies the label and reference text, so a new external system
 *  reaches the References card without a frontend change.
 *
 *  `type` is deliberately open: the server owns the set (`fareharborBooking` and
 *  `stripePayment` today), and pinning it to a literal here would make the
 *  no-frontend-change property above false the moment the server adds one. */
export interface BookingExternalLink {
  type: string;
  label: string;
  reference: string | null;
  url: string;
}

/** Mirrors the frozen `GET /api/v2/bookings/:id` response (unwrapped). */
export interface BookingDetail {
  id: string;
  confirmationCode: string | null;
  /** The ref to show staff: `confirmationCode`, else a migrated booking's FareHarbor display id. */
  displayRef: string | null;
  status: BookingStatus;
  isComp: boolean;
  source: BookingSource;
  createdAt: string;
  currency: string;
  money: BookingMoney;
  /** null on comps AND on migrated records without stripe ids. */
  stripe: BookingStripe | null;
  customer: BookingCustomer;
  workshop: BookingWorkshop;
  chain: BookingChain;
  /** Empty when the booking has no external references. */
  links: BookingExternalLink[];
  rebook: BookingRebookCutoff;
  /** Null unless this booking has been refunded. */
  refund: BookingRefund | null;
  /** The passes issued by refunding this booking to gift passes, codes masked.
   *  Empty otherwise. */
  giftPassesIssued: BookingIssuedGiftPass[];
  /** The gift passes that paid for seats on this booking; empty when none did. */
  giftPassesRedeemed: BookingGiftPass[];
  /** The gift cards that paid toward this booking; empty when none did. */
  giftCardsRedeemed: BookingGiftCardRedemption[];
}

/** A gift card that paid toward a booking. `amountCents` is what it paid. */
export interface BookingGiftCardRedemption {
  giftCardId: string;
  codeMasked: string;
  amountCents: number;
}

export type GiftPassStatus = 'ISSUED' | 'SENT' | 'REDEEMED' | 'VOIDED' | 'EXPIRED';

export interface BookingIssuedGiftPass {
  id: string;
  codeMasked: string;
  status: GiftPassStatus;
  createdAt: string;
}

/** Thrown by {@link fetchBookingDetail} so callers can branch on 404 vs. other. */
export class BookingFetchError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'BookingFetchError';
  }
}

export async function fetchBookingDetail(id: string): Promise<BookingDetail> {
  const res = await apiFetch(`/api/v2/bookings/${id}`);
  if (!res.ok) {
    // 400 = malformed id (e.g. a legacy `BKG-…` bookmark, not a UUID). Treat it
    // as "not found" so it lands on the friendly state rather than a raw error.
    const msg =
      res.status === 404 || res.status === 400
        ? 'Booking not found'
        : res.status === 403
          ? 'You do not have access to this booking'
          : `Failed to load booking: ${res.status}`;
    throw new BookingFetchError(msg, res.status);
  }
  return (await res.json()) as BookingDetail;
}

/** Non-sensitive booking config — whether checkout adds a service fee, so the
 *  create-booking modal knows whether to mention one, and whether gift passes
 *  are on, which decides whether a refund goes to passes or a gift card. */
export interface BookingConfig {
  serviceFeeEnabled: boolean;
  giftPassesEnabled: boolean;
}

export async function fetchBookingConfig(): Promise<BookingConfig> {
  const res = await apiFetch('/api/v2/bookings/config');
  if (!res.ok) throw new Error(`Failed to load booking config: ${res.status}`);
  return (await res.json()) as BookingConfig;
}

/** Hold-aware bookable seats for one workshop (BON-142): `available` here is
 *  capacity − confirmed − active holds (what can actually be booked), unlike
 *  the workshops list's confirmed-only figure. */
export interface WorkshopAvailability {
  available: number;
  capacity: number;
  confirmedSeats: number;
  heldSeats: number;
}

/** Max workshop ids per GET request. The ids ride in the query string, so the
 *  URL must stay well under the server's ~16KB request-header limit (a full set
 *  of 500 UUIDs is ~18KB → HTTP 431). 100 ids ≈ a 3.7KB URL — safe even with a
 *  ~1KB `Authorization` header. Larger sets are chunked across requests. The
 *  server independently caps each request at 200. */
const AVAILABILITY_CHUNK = 100;

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

async function fetchAvailabilityChunk(
  ids: string[],
): Promise<Record<string, WorkshopAvailability>> {
  const res = await apiFetch(
    `/api/v2/bookings/availability?ids=${encodeURIComponent(ids.join(','))}`,
  );
  if (!res.ok) throw new Error(`Failed to load availability: ${res.status}`);
  const body = (await res.json()) as { availability: Record<string, WorkshopAvailability> };
  return body.availability;
}

/** GET /api/v2/bookings/availability?ids=… — hold-aware availability for a set
 *  of workshops, keyed by workshopId. Unknown ids are simply absent from the
 *  map. Batched server-side; the CSR picker overlays this onto the workshops it
 *  fetched so "seats left" reflects active holds, not just confirmed bookings.
 *  Ids are chunked (see {@link AVAILABILITY_CHUNK}) so no single URL grows long
 *  enough to exceed the server header limit; the chunk maps are merged. */
export async function fetchBookableAvailability(
  workshopIds: string[],
): Promise<Record<string, WorkshopAvailability>> {
  if (workshopIds.length === 0) return {};
  const maps = await Promise.all(
    chunk(workshopIds, AVAILABILITY_CHUNK).map((ids) => fetchAvailabilityChunk(ids)),
  );
  return Object.assign({}, ...maps) as Record<string, WorkshopAvailability>;
}

// ─── CSR manual booking creation (BON-142) ─────────────────────────────────
//
// Two POST clients backing the "New booking" modal (comp OR Stripe payment
// link). Mirrors the frozen `bookings-v2.routes.ts` contract exactly —
// customer carries name + email ONLY (no phone/consent; see the route's
// header for why the CSR-mediated flow can't collect either).

/** Customer identity the CSR collects — name + email only. */
export interface CsrCustomerInput {
  email: string;
  /** Optional single "Full name" field; split into first/last server-side. */
  fullName?: string;
}

export interface CreateCompBookingInput {
  workshopId: string;
  attendeeCount: number;
  /** Required — an unattributed free booking is an abuse vector (BON-143). */
  reason: string;
  customer: CsrCustomerInput;
}

export interface CreateCompBookingResult {
  bookingId: string;
  confirmationCode: string | null;
  status: 'CONFIRMED';
}

/** Thrown by the CSR booking creates so callers can render an inline message
 *  keyed on the HTTP status, per the route's error contract. */
export class CsrBookingError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'CsrBookingError';
  }
}

/** Map a failed create response to a {@link CsrBookingError}. Prefers the
 *  server's own message (the route already returns human-readable copy for
 *  each of these); falls back to canned copy for the known statuses, or a
 *  generic message otherwise. */
async function csrBookingErrorFromResponse(res: Response): Promise<CsrBookingError> {
  const body = (await res.json().catch(() => ({}))) as { message?: string };
  const fallback =
    res.status === 409
      ? 'Not enough capacity for this workshop'
      : res.status === 422
        ? 'This workshop has already ended and can no longer be booked'
        : res.status === 403
          ? "You don't have access to this workshop's region"
          : res.status === 404
            ? 'Workshop not found or not bookable'
            : res.status === 400
              ? 'Please check the booking details and try again.'
              : `Failed to create booking: ${res.status}`;
  return new CsrBookingError(body.message || fallback, res.status);
}

/** POST /api/v2/bookings/comp — a free booking born CONFIRMED. Gated
 *  server-side on `write:comp-bookings`. */
export async function createCompBooking(
  input: CreateCompBookingInput,
): Promise<CreateCompBookingResult> {
  const res = await apiFetch('/api/v2/bookings/comp', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await csrBookingErrorFromResponse(res);
  return (await res.json()) as CreateCompBookingResult;
}

// The CSR payment-link client lived here. BON-382 replaced it with the
// reservation flow in `api/reservations.ts`, which posts to a route that
// actually exists.

// ─── Refund to a gift card, the original card, or another rail (BON-147, BON-335)
//
// `RefundMethod` itself is declared earlier, alongside `BookingDetail.refund`.

/** Mirrors the frozen `DELETE /api/v2/bookings/:id` 200 envelope — the single
 *  mechanism for retiring a booking (a paid one is refunded; a comp is
 *  cancelled, BON-337's mechanism). Only `codeMasked` may cross this
 *  boundary — there is no field here for a raw gift-card code.
 *
 *  `giftCard` and `emailSent` are `null`, not absent, on a `CARD`/`EXTERNAL`
 *  refund — no card was minted and no email was owed, so there is a value to
 *  carry rather than a key to omit. Branch on `method`, not on the nullness
 *  of either field: `emailSent: false` (gift-card, delivery failed) and
 *  `emailSent: null` (recorded, no email ever owed) are NOT interchangeable. */
export interface RefundBookingResult {
  refund: {
    id: string;
    bookingId: string;
    amountCents: number;
    currency: string;
    method: RefundMethod;
    reason: string;
    createdAt: string;
    /** The Stripe refund id, cheque number or transfer id, where the CSR gave
     *  one. Null for a gift-card refund, and whenever none was entered. */
    externalReference: string | null;
    giftCard: { id: string; codeMasked: string; balanceCents: number } | null;
    /** One per seat on a `GIFT_PASS` refund, masked; null on every other method. */
    giftPasses: { id: string; codeMasked: string }[] | null;
    /** `null` on a `GIFT_PASS` refund too: its email is queued, not sent inline. */
    emailSent: boolean | null;
  };
  booking: { id: string; status: 'REFUNDED'; amountPaidCents: 0 };
}

/** Thrown by {@link refundBooking}. `code` carries the machine-readable 409
 *  reasons (`ALREADY_REFUNDED`, `NOT_CONFIRMED`, `NOTHING_PAID`) so callers can
 *  branch on settled-state vs. a real error. */
export class RefundBookingError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'RefundBookingError';
  }
}

async function refundBookingErrorFromResponse(res: Response): Promise<RefundBookingError> {
  const body = (await res.json().catch(() => ({}))) as { message?: string; code?: string };
  // Branch on the CODE before the status. Three distinct rejections share 409
  // — already refunded, not confirmed, nothing paid — and collapsing them onto
  // the status alone made "already refunded" the fallback for all three. That
  // is the one of the three the UI renders as a SETTLED receipt rather than an
  // error, so mistaking either of the others for it reports a refund that
  // never happened.
  const byCode: Record<string, string> = {
    ALREADY_REFUNDED: 'This booking has already been refunded',
    NOT_CONFIRMED: 'Only a confirmed booking can be refunded',
    NOTHING_PAID: 'This booking has no recorded payment to refund',
    NO_PAYMENT_INTENT: 'This booking has no card payment on file to refund it to',
    NO_CUSTOMER_EMAIL: 'Add an email address for this customer so the gift passes can be sent',
    REFUND_METHOD_UNAVAILABLE: 'That refund option is not available right now',
  };
  const fallback =
    (body.code ? byCode[body.code] : undefined) ??
    (res.status === 409
      ? 'This booking cannot be refunded'
      : res.status === 404
        ? 'Booking not found'
        : res.status === 403
          ? "You don't have access to this booking's region"
          : res.status === 400
            ? 'Please say why this booking is being refunded'
            : `Failed to refund booking: ${res.status}`);
  return new RefundBookingError(body.message || fallback, res.status, body.code);
}

/** The request this client sends — always an explicit `method`, mirroring the
 *  frozen discriminated union server-side (`{ method: 'GIFT_CARD', reason }`
 *  vs. `{ method: 'CARD' | 'EXTERNAL', reason, externalReference? }`).
 *  `method` is REQUIRED with no default on this endpoint: HTTP permits an
 *  intermediary to drop a DELETE body, and a stripped body defaulting to
 *  `GIFT_CARD` (the retired POST /refund endpoint's behavior, kept for an
 *  in-flight old bundle) would mint and email a live bearer code for a
 *  refund already paid by another rail. The server 400s a missing or
 *  partial body instead — this client must never construct one. */
export interface RefundBookingInput {
  method: RefundMethod;
  reason: string;
  /** Only meaningful for `CARD`/`EXTERNAL`. Never sent on a `GIFT_CARD`
   *  request, even if supplied here — a `GIFT_CARD` refund with a reference is
   *  a 400, not a silent strip, so this client never manufactures that case. */
  externalReference?: string;
}

/** DELETE /api/v2/bookings/:id — gated server-side on `refund:bookings`. The
 *  single mechanism for retiring a booking; a comp is cancelled through
 *  BON-337's mechanism instead. The amount is never client-supplied, for any
 *  destination: the server computes it from the booking's net cash and
 *  returns it in the envelope above.
 *
 *  The method-mismatch guard in `useRefundBookingSubmit.ts` — comparing the
 *  returned `refund.method` against what was requested — stays in place
 *  unchanged: it is still the right defence against a stale API accepting a
 *  narrower body than this client sends, and now also catches a response
 *  from any handler this request was not meant to reach. */
export async function refundBooking(
  id: string,
  input: RefundBookingInput,
): Promise<RefundBookingResult> {
  const body: { method: RefundMethod; reason: string; externalReference?: string } =
    input.method === 'GIFT_CARD' || input.method === 'GIFT_PASS'
      ? { method: input.method, reason: input.reason }
      : {
          method: input.method,
          reason: input.reason,
          ...(input.externalReference ? { externalReference: input.externalReference } : {}),
        };
  const res = await apiFetch(`/api/v2/bookings/${id}`, {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await refundBookingErrorFromResponse(res);
  return (await res.json()) as RefundBookingResult;
}

/* eslint-enable max-lines -- end of the temporary hotfix exemption above */
